(() => {
  const normalizeColor = value => String(value || '').trim().toLowerCase().replace(/gray/g, 'grey').replace(/\s+/g, ' ');
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const fetchWithTimeout = (url, options, timeoutMs, consume = response => response) => {
    const controller = new AbortController();
    const sourceSignal = options.signal;
    const abort = () => controller.abort();
    let timedOut = false;
    if (sourceSignal?.aborted) abort();
    else sourceSignal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(() => { timedOut = true; abort(); }, timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).then(consume).catch(error => {
      if (!timedOut) throw error;
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }).finally(() => {
      clearTimeout(timeout);
      sourceSignal?.removeEventListener('abort', abort);
    });
  };
  const safeChartTags = new Set(['a', 'b', 'br', 'caption', 'div', 'em', 'h2', 'h3', 'h4', 'i', 'img', 'li', 'ol', 'p', 'span', 'strong', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul']);
  const safeChartClasses = new Set(['size-chart-container', 'size-chart-title', 'size-chart-table', 'size-chart-th', 'size-chart-td', 'sz-chart-p']);
  const safeChartId = /^[A-Za-z][A-Za-z0-9_.:-]{0,127}$/;
  let chartIdSequence = 0;
  const isSizeOption = option => /size|waist/i.test(option.name);
  const normalizeOptionValue = value => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const chartUrl = value => {
    try {
      if (!value?.trim()) return '';
      const url = new URL(value.trim(), location.origin);
      return url.protocol === 'https:' || (url.protocol === 'http:' && url.origin === location.origin) ? url.href : '';
    } catch {
      return '';
    }
  };
  const storefrontProductUrl = (value, variantId) => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !['http:', 'https:'].includes(url.protocol) || url.username || url.password) return '';
      if (variantId) url.searchParams.set('variant', String(variantId));
      return url.href;
    } catch {
      return '';
    }
  };
  const copyChartNodes = (source, target, headerIds) => {
    for (const child of source.childNodes) {
      if (child.nodeType === 3) {
        target.append(document.createTextNode(child.nodeValue));
        continue;
      }
      if (child.nodeType !== 1 || !safeChartTags.has(child.localName)) continue;
      const copy = document.createElement(child.localName);
      const safeClasses = [...child.classList].filter(className => safeChartClasses.has(className));
      if (safeClasses.length) copy.classList.add(...safeClasses);
      for (const attribute of ['alt', 'title']) if (child.hasAttribute(attribute)) copy.setAttribute(attribute, child.getAttribute(attribute));
      if (child.localName === 'td' || child.localName === 'th') {
        if (child.localName === 'th' && headerIds.has(child.getAttribute('id'))) copy.id = headerIds.get(child.getAttribute('id'));
        const headers = (child.getAttribute('headers') || '').split(/\s+/).map(id => headerIds.get(id)).filter(Boolean);
        if (headers.length) copy.setAttribute('headers', headers.join(' '));
        for (const attribute of ['colspan', 'rowspan']) {
          const value = Number(child.getAttribute(attribute));
          if (Number.isInteger(value) && value > 0 && value <= 50) copy.setAttribute(attribute, String(value));
        }
        if (child.localName === 'th' && ['row', 'col', 'rowgroup', 'colgroup'].includes(child.getAttribute('scope'))) copy.setAttribute('scope', child.getAttribute('scope'));
      }
      if (child.localName === 'a') {
        const href = chartUrl(child.getAttribute('href') || '');
        if (href) copy.href = href;
        if (child.getAttribute('target') === '_blank') {
          copy.target = '_blank';
          copy.rel = 'noopener noreferrer';
        }
      }
      if (child.localName === 'img') {
        const src = chartUrl(child.getAttribute('src') || '');
        if (!src) continue;
        copy.src = src;
        if (!child.hasAttribute('alt')) copy.setAttribute('alt', '');
        copy.loading = 'lazy';
        copy.decoding = 'async';
        for (const dimension of ['width', 'height']) {
          const value = Number(child.getAttribute(dimension));
          if (Number.isInteger(value) && value > 0 && value <= 2000) copy.setAttribute(dimension, String(value));
        }
      }
      copyChartNodes(child, copy, headerIds);
      target.append(copy);
    }
  };
  const sanitizeChart = source => {
    if (!source) return null;
    const safe = document.createElement('div');
    safe.className = 'complete-set__chart-content';
    const idCounts = new Map();
    for (const element of source.querySelectorAll('[id]')) {
      const id = element.getAttribute('id');
      if (safeChartId.test(id)) idCounts.set(id, (idCounts.get(id) || 0) + 1);
    }
    const validHeaderIds = [...source.querySelectorAll('th[id]')]
      .map(header => header.getAttribute('id'))
      .filter(id => idCounts.get(id) === 1);
    let prefix;
    do { prefix = `complete-set-chart-${++chartIdSequence}`; } while (validHeaderIds.some(id => document.getElementById(`${prefix}-${id}`)));
    const headerIds = new Map(validHeaderIds.map(id => [id, `${prefix}-${id}`]));
    copyChartNodes(source, safe, headerIds);
    return safe.textContent.trim() || safe.querySelector('table, img, a, ul, ol') ? safe : null;
  };
  const readChartMarkup = script => {
    try {
      const template = document.createElement('template');
      template.innerHTML = JSON.parse(script.textContent);
      return sanitizeChart(template.content);
    } catch {
      return null;
    }
  };

  class CompleteSet extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = document.getElementById(this.getAttribute('form'));
      if (!this.form) return;
      this.querySelector('[data-set-rows]').replaceChildren();
      this.controller = new AbortController();
      this.cache = new Map();
      this.strings = JSON.parse(this.querySelector('[data-set-strings]').textContent);
      const main = JSON.parse(this.querySelector('[data-set-main]').textContent);
      this.mainColorIndex = main.colorIndex;
      this.mainColor = main.options?.[main.colorIndex] || '';
      this.mainSizeIndex = main.sizeIndex;
      this.mainSize = Number.isInteger(this.mainSizeIndex) ? main.options?.[this.mainSizeIndex] || '' : '';
      this.mainAvailable = main.available;
      this.mainPrice = Number(this.dataset.mainPrice);
      this.mainSizeChosen = !this.mainSizeRequiresChoice || new URL(location.href).searchParams.get('variant') === this.form.elements.namedItem('id')?.value;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.rows = ['top', 'bottom', 'accessory'].map(group => {
        const products = [...this.querySelectorAll('[data-set-candidate]')].filter(item => item.dataset.group === group).map(item => ({ ...item.dataset, image: item.querySelector('img')?.getAttribute('src') }));
        if (!products.length) return null;
        const row = { group, products, product: products[0], included: true, choices: [], manualColor: false, manualSize: false, revision: 0 };
        this.createRow(row);
        return row;
      }).filter(Boolean);
      this.querySelector('[data-set-candidates]').hidden = true;
      this.form.addEventListener('variant:selection-cleared', () => this.clearMainSelection(), { signal: this.controller.signal });
      this.form.addEventListener('variant:change', event => {
        this.mainSelectionCleared = false;
        this.mainAvailable = !!event.detail.variant?.available;
        if (!this.mainSizeRequiresChoice) this.mainSizeChosen = true;
        this.mainPrice = Number(event.detail.variant?.price || 0);
        this.mainColor = event.detail.variant?.options?.[this.mainColorIndex] || '';
        this.mainSize = event.detail.variant?.options?.[this.mainSizeIndex] || '';
        for (const row of this.rows) {
          const resume = row.waitingForMain;
          row.waitingForMain = false;
          if (resume || !row.manualColor || !row.manualSize) this.resolve(row, !row.manualColor);
        }
        this.syncButtons();
      }, { signal: this.controller.signal });
      this.form.addEventListener('change', event => {
        if (event.target.name?.startsWith('option') && event.target.closest('.option_downcase-size')) this.mainSizeChosen = event.target.type === 'radio' ? event.target.checked : !!event.target.value;
        this.syncButtons();
      }, { signal: this.controller.signal });
      // Variant inputs associated with this form may live outside it.
      this.scope = this.closest('.shopify-section') || this.parentElement;
      this.scope.addEventListener('change', event => {
        if (event.target.form === this.form && event.target.closest('.option_downcase-size')) this.mainSizeChosen = event.target.type === 'radio' ? event.target.checked : !!event.target.value;
        this.syncButtons();
      }, { signal: this.controller.signal });
      this.scope.addEventListener('click', event => {
        const input = event.target.closest('label')?.control || event.target.closest('input');
        if (input?.form !== this.form || !input?.closest('.option_downcase-size') || input.disabled) return;
        queueMicrotask(() => {
          // Clicking the initially checked size emits no change event.
          if (input.checked) { this.mainSizeChosen = true; this.syncButtons(); }
        });
      }, { signal: this.controller.signal });
      this.scope.addEventListener('input', () => this.syncButtons(), { signal: this.controller.signal });
      this.planObserver = new MutationObserver(() => this.syncButtons());
      this.planObserver.observe(this.form, { subtree: true, attributes: true, attributeFilter: ['value'], childList: true });
      this.querySelector('[data-set-status]').textContent = '';
      for (const row of this.rows) this.resolve(row, true);
      this.syncButtons();
    }

    clearMainSelection() {
      this.mainSelectionCleared = true;
      this.mainAvailable = false;
      this.mainSizeChosen = false;
      this.mainColor = '';
      this.mainSize = '';
      this.mainPrice = Number(this.dataset.mainPrice);
      for (const row of this.rows) {
        // Retain manual preferences, but no resolved variant survives a cleared main.
        row.revision++;
        row.waitingForMain = true;
        row.variant = null;
        row.loading = false;
        row.error = false;
        row.similar = false;
        row.unavailableChoice = '';
        this.renderSummary(row);
      }
      this.querySelector('[data-set-status]').textContent = this.strings.chooseMain;
      this.syncButtons();
    }

    get mainSizeRequiresChoice() {
      const field = this.form.closest('.product')?.querySelector('.option_downcase-size');
      // Singleton dimensions are automatically selected and may be hidden by the picker.
      return !!field && field.querySelectorAll('input[type="radio"], option:not([value=""]), button[data-option-value]').length > 1;
    }

    get mainVariantId() {
      const ids = new FormData(this.form).getAll('id');
      return ids.length === 1 && typeof ids[0] === 'string' && ids[0].trim() ? ids[0] : '';
    }

    get mainSelectionReady() {
      return this.mainAvailable && this.mainSizeChosen && !!this.mainVariantId;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.planObserver?.disconnect();
      this.rows?.forEach(row => { row.included = false; });
      this.syncButtons();
      this.scope?.classList.remove('complete-set-has-items');
      this.controller = null;
    }

    text(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`__${name}__`, value), this.strings[key] || '');
    }

    get subscriptionSelected() {
      return !!new FormData(this.form).get('selling_plan');
    }

    get hasIncludedItems() {
      return !this.subscriptionSelected && !!this.rows?.some(row => row.included);
    }

    createRow(row) {
      row.element = node('article', 'complete-set__row');
      row.element.dataset.category = row.group;
      const summary = node('div', 'complete-set__summary');
      const label = node('label', 'complete-set__include');
      row.checkbox = node('input');
      row.checkbox.type = 'checkbox';
      row.checkbox.checked = true;
      label.append(row.checkbox);
      row.image = node('img', 'complete-set__image');
      row.image.width = 80;
      row.image.height = 100;
      row.image.loading = 'lazy';
      const description = node('div', 'complete-set__description');
      row.link = node('a', 'complete-set__title');
      row.selection = node('p', 'complete-set__selection');
      row.price = node('p', 'complete-set__price');
      row.change = node('button', 'complete-set__change complete-set__change-link', this.text('change', { CATEGORY: this.strings[row.group] }));
      row.change.type = 'button';
      row.details = node('div', 'complete-set__adjustments');
      row.details.id = `${this.form.getAttribute('id')}-set-${row.group}`;
      row.details.hidden = true;
      row.change.setAttribute('aria-controls', row.details.id);
      row.change.setAttribute('aria-expanded', 'false');
      row.message = node('p', 'complete-set__message');
      row.message.id = `${row.details.id}-message`;
      row.checkbox.setAttribute('aria-describedby', row.message.id);
      row.options = node('div');
      row.message.setAttribute('role', 'status');
      row.details.append(row.options);
      description.append(row.link, row.selection, row.price, row.change);
      summary.append(label, row.image, description);
      row.element.append(summary, row.message, row.details);
      this.querySelector('[data-set-rows]').append(row.element);
      row.checkbox.addEventListener('change', () => {
        row.included = row.checkbox.checked;
        this.renderSummary(row);
        this.syncButtons();
      }, { signal: this.controller.signal });
      row.change.addEventListener('click', () => this.openRow(row, row.details.hidden), { signal: this.controller.signal });
      this.renderSummary(row);
    }

    openRow(row, open = true) {
      row.details.hidden = !open;
      row.change.setAttribute('aria-expanded', String(open));
    }

    async load(url, ids) {
      const resolvedUrl = storefrontProductUrl(url);
      if (!resolvedUrl) throw new Error('Invalid product URL');
      const target = new URL(resolvedUrl);
      target.searchParams.delete('variant');
      target.searchParams.set('section_id', 'complete-set-product');
      if (ids?.length) target.searchParams.set('option_values', ids.join(','));
      const key = target.href;
      if (!this.cache.has(key)) {
        const request = fetchWithTimeout(key, { signal: this.controller.signal }, 8000, async response => {
          if (!response.ok) throw new Error('Section unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const data = document.querySelector('[data-set-product]');
          if (!data) throw new Error('Product unavailable');
          const chart = document.querySelector('script[data-set-chart]');
          return { ...JSON.parse(data.textContent), chart: readChartMarkup(chart) };
        }).catch(error => { this.cache.delete(key); throw error; });
        this.cache.set(key, request);
      }
      return this.cache.get(key);
    }

    matchColor(option) {
      const main = normalizeColor(this.mainColor);
      const available = option.values.filter(value => value.available === true);
      const exact = available.find(value => normalizeColor(value.name) === main);
      if (exact) return { value: exact, similar: false };
      for (const line of (this.dataset.colorMap || '').split('\n')) {
        const [from, to] = line.split('=').map(normalizeColor);
        if (from !== main || !to) continue;
        const value = available.find(value => normalizeColor(value.name) === to);
        if (value) return { value, similar: true };
      }
      return null;
    }

    async resolve(row, followColor = false) {
      if (row.waitingForMain) return;
      const revision = ++row.revision;
      row.loading = true;
      row.error = false;
      row.unavailableChoice = '';
      row.variant = null;
      this.renderSummary(row);
      this.syncButtons();
      try {
        let data = await this.load(row.product.url);
        if (revision !== row.revision || !this.isConnected) return;
        const old = row.data;
        const previousChoices = data.options.map(option => {
          const previous = old?.options.find(item => item.name === option.name);
          const previousChoice = previous && row.choices[old.options.indexOf(previous)];
          // Availability here belongs to Shopify's default context, not the requested combination.
          const sameValue = option.values.find(value => value.name === previousChoice?.name);
          if (option.color && (followColor || !row.manualColor)) return null;
          if (isSizeOption(option) && !row.manualSize && this.mainSize) {
            const matchingSize = option.values.find(value => normalizeOptionValue(value.name) === normalizeOptionValue(this.mainSize));
            if (matchingSize) return matchingSize;
          }
          if (sameValue) return sameValue;
          const selectedValue = option.values.find(value => value.selected && value.available !== false);
          return selectedValue || option.values.find(value => value.available === true) || (option.values.length === 1 && option.values[0].available !== false ? option.values[0] : null);
        });
        const colorIndex = data.options.findIndex(option => option.color);
        const contextIds = data.options.map((option, index) => previousChoices[index]?.id || option.values.find(value => value.selected)?.id || option.values[0]?.id);
        const defaultIds = data.options.map(option => (option.values.find(value => value.selected && value.available !== false) || option.values.find(value => value.available === true) || option.values[0])?.id);
        const hasContextChoice = previousChoices.some(Boolean);
        if (contextIds.length && (hasContextChoice || contextIds.some((id, index) => id !== defaultIds[index]))) data = await this.load(row.product.url, contextIds);
        if (revision !== row.revision || !this.isConnected) return;
        // Resolve automatic colors after Shopify has calculated availability for the chosen preceding dimensions.
        row.choices = data.options.map((option, index) => option.color && (followColor || !row.manualColor) ? null : option.values.find(value => value.id === previousChoices[index]?.id) || null);
        if (colorIndex >= 0 && (followColor || !row.manualColor)) {
          const match = this.matchColor(data.options[colorIndex]);
          row.similar = match?.similar || false;
          row.choices[colorIndex] = match?.value || null;
        }
        const finalIds = data.options.map((option, index) => row.choices[index]?.id || (option.values.find(value => value.selected && value.available !== false) || option.values.find(value => value.available === true) || option.values[0])?.id);
        const selectedIds = data.options.map(option => (option.values.find(value => value.selected && value.available !== false) || option.values.find(value => value.available === true) || option.values[0])?.id);
        if (finalIds.length && finalIds.some((id, index) => id !== selectedIds[index])) data = await this.load(row.product.url, finalIds);
        if (revision !== row.revision || !this.isConnected) return;
        row.data = data;
        row.choices = data.options.map((option, index) => option.values.find(value => value.id === row.choices[index]?.id) || null);
        const complete = row.choices.every(Boolean);
        const exactVariant = row.choices.length === 0 ? data.variant?.options?.length === 1 && data.variant.options[0] === 'Default Title' : data.variant?.options?.length === row.choices.length && data.variant.options.every((value, index) => value === row.choices[index]?.name);
        row.variant = complete && exactVariant && data.variant.available && !data.variant.requiresComponents && data.variant.quantityMinimum <= 1 && data.variant.quantityIncrement === 1 ? data.variant : null;
        // Keep intent on an unavailable combination so changing color never substitutes a size.
        this.renderOptions(row);
      } catch (error) {
        if (error.name === 'AbortError' || revision !== row.revision || !this.isConnected) return;
        row.error = true;
        this.renderOptions(row);
      } finally {
        if (revision === row.revision && this.isConnected) {
          row.loading = false;
          this.renderSummary(row);
          if (row.included && !row.variant) this.openRow(row);
          this.syncButtons();
        }
      }
    }

    async selectOption(row, index, value) {
      if (this.busy) return;
      row.unavailableChoice = '';
      if (value.available === false) {
        row.unavailableChoice = `${row.data.options[index].name} ${value.name}: ${window.themeVariables.strings.soldOutButton}`;
        row.variant = null;
        this.renderSummary(row);
        this.syncButtons();
        return;
      }
      if (row.data.options[index].color) {
        row.manualColor = true;
        row.similar = false;
      }
      if (isSizeOption(row.data.options[index])) row.manualSize = true;
      if (value.url && new URL(value.url, location.origin).pathname !== new URL(row.product.url, location.origin).pathname) {
        const related = row.products.find(product => new URL(product.url, location.origin).pathname === new URL(value.url, location.origin).pathname);
        if (!related) { row.variant = null; row.error = true; this.renderSummary(row); this.syncButtons(); return; }
        row.product = related;
      }
      row.choices[index] = value;
      // The resolver preserves the chosen names across the contextual section refresh.
      await this.resolve(row);
    }

    renderOptions(row) {
      const focused = document.activeElement;
      const focusKey = row.options.contains(focused) ? focused.dataset.focusKey : null;
      const restoreFocus = () => {
        if (!focusKey) return;
        const focusTarget = [...row.options.querySelectorAll('[data-focus-key]')].find(button => button.dataset.focusKey === focusKey);
        const fallback = focusKey === 'retry-options'
          ? row.options.querySelector('button:not(:disabled)') || row.change
          : null;
        (focusTarget || fallback)?.focus({ preventScroll: true });
      };
      row.options.replaceChildren();
      if (row.products.length > 1) {
        const field = node('fieldset', 'complete-set__field');
        field.append(node('legend', '', this.strings.style));
        const choices = node('div', 'complete-set__choices');
        for (const product of row.products) {
          const words = row.products.map(item => item.title.split(/\s+/));
          let start = 0;
          let end = 0;
          while (words.every(parts => parts[start] && parts[start] === words[0][start])) start++;
          while (words.every(parts => parts.length - end > start && parts.at(-end - 1) === words[0].at(-end - 1))) end++;
          const label = product.title.split(/\s+/).slice(start, end ? -end : undefined).join(' ') || product.title;
          const button = node('button', 'complete-set__option', label);
          button.setAttribute('aria-label', product.title);
          button.type = 'button';
          button.setAttribute('aria-pressed', String(product.id === row.product.id));
          button.dataset.focusKey = `style-${product.id}`;
          button.addEventListener('click', () => {
            if (this.busy) return;
            row.product = product;
            this.resolve(row, !row.manualColor);
          });
          choices.append(button);
        }
        field.append(choices);
        row.options.append(field);
      }
      if (row.error) {
        const retry = node('button', 'complete-set__change', this.strings.retry);
        retry.type = 'button';
        retry.dataset.focusKey = 'retry-options';
        retry.addEventListener('click', () => this.resolve(row, !row.manualColor));
        row.options.append(retry);
        restoreFocus();
        return;
      }
      let sizeField = null;
      for (const [index, option] of (row.data?.options || []).entries()) {
        const isSize = isSizeOption(option);
        const field = node(isSize ? 'div' : 'fieldset', 'complete-set__field');
        if (isSize) {
          field.classList.add('complete-set__field--size');
          const label = `${option.name}: ${row.choices[index]?.name || this.text('choose', { OPTION: option.name })}`;
          field.setAttribute('role', 'group');
          field.setAttribute('aria-label', label);
          field.append(node('div', 'complete-set__field-label', label));
          sizeField = field;
        } else {
          if (option.color) field.classList.add('complete-set__field--color');
          field.append(node('legend', '', `${option.name}: ${row.choices[index]?.name || this.text('choose', { OPTION: option.name })}`));
        }
        field.dataset.optionIndex = index;
        const choices = node('div', 'complete-set__choices');
        for (const value of option.values) {
          const button = node('button', `complete-set__option${option.color ? ' complete-set__color' : ''}`);
          button.type = 'button';
          button.dataset.focusKey = `option-${index}-${value.id}`;
          button.setAttribute('aria-pressed', String(row.choices[index]?.id === value.id));
          if (value.available === false) {
            const soldOut = window.themeVariables.strings.soldOutButton;
            button.classList.add('complete-set__option--unavailable');
            button.setAttribute('aria-label', `${value.name}, ${soldOut}`);
            button.title = soldOut;
          }
          if (option.color && value.image) {
            const image = node('img');
            image.src = value.image;
            image.width = 44;
            image.height = 54;
            image.alt = '';
            image.loading = 'lazy';
            button.append(image);
          }
          button.append(node('span', '', value.name));
          button.addEventListener('click', () => this.selectOption(row, index, value));
          choices.append(button);
        }
        field.append(choices);
        if (option.color && row.manualColor) {
          const reset = node('button', 'complete-set__change', this.strings.match);
          reset.type = 'button';
          reset.addEventListener('click', () => { row.manualColor = false; this.resolve(row, true); });
          field.append(reset);
        }
        row.options.append(field);
      }
      if (row.data?.chart) {
        const chartId = `${row.details.id}-chart`;
        const chartToggle = node('button', 'complete-set__chart-toggle');
        chartToggle.type = 'button';
        chartToggle.setAttribute('aria-expanded', 'false');
        chartToggle.setAttribute('aria-controls', chartId);
        chartToggle.append(node('span', 'complete-set__chart-label', this.strings.sizeChart));
        const chart = row.data.chart.cloneNode(true);
        chart.classList.add('complete-set__chart');
        chart.id = chartId;
        chart.hidden = true;
        chartToggle.addEventListener('click', () => {
          const expanded = chartToggle.getAttribute('aria-expanded') !== 'true';
          chartToggle.setAttribute('aria-expanded', String(expanded));
          chart.hidden = !expanded;
        });
        if (sizeField) {
          sizeField.insertBefore(chartToggle, sizeField.querySelector('.complete-set__choices'));
          sizeField.append(chart);
        } else row.options.append(chartToggle, chart);
      }
      restoreFocus();
    }

    missing(row) {
      if (row.waitingForMain) return this.strings.chooseMain;
      if (row.loading) return this.strings.loading;
      if (row.error) return this.strings.loadError;
      if (row.unavailableChoice) return row.unavailableChoice;
      if (!row.data) return this.strings.loading;
      const index = row.data.options.findIndex((option, index) => !row.choices[index]);
      if (index >= 0) {
        const option = row.data.options[index];
        return option.color ? this.text('noMatch', { TITLE: row.product.title }) : `${row.product.title}: ${this.text('choose', { OPTION: option.name })}`;
      }
      return row.variant ? '' : this.strings.unavailable;
    }

    renderSummary(row) {
      row.checkbox.setAttribute('aria-label', this.text('include', { TITLE: row.product.title }));
      row.link.textContent = row.product.title;
      const productHref = storefrontProductUrl(row.product.url, row.variant?.id);
      if (productHref) row.link.href = productHref;
      else row.link.removeAttribute('href');
      const colorIndex = row.data?.options.findIndex(option => option.color) ?? -1;
      const color = row.choices[colorIndex];
      const image = row.variant?.image || color?.image || row.data?.image || row.product.image;
      if (image) row.image.src = image;
      row.image.hidden = !image;
      row.image.alt = row.variant ? row.product.title : this.strings.preview;
      row.selection.textContent = row.waitingForMain ? this.strings.preview : row.choices.filter(Boolean).map(value => value.name).join(' · ') || this.strings.preview;
      row.price.textContent = this.money.format(Number(row.variant?.price ?? row.product.price) / 100);
      row.message.textContent = this.missing(row) || (row.similar && color ? this.text('similar', { COLOR: color.name }) : '');
      row.element.classList.toggle('complete-set__row--excluded', !row.included);
      row.element.setAttribute('aria-busy', String(!!row.loading));
    }

    syncButtons() {
      if (!this.rows || !this.form) return;
      const subscription = this.subscriptionSelected;
      this.querySelector('[data-set-disclosure]').hidden = subscription;
      const status = this.querySelector('[data-set-status]');
      if (subscription) status.textContent = this.strings.subscription;
      else if (status.textContent === this.strings.subscription) status.textContent = '';
      if (this.mainSelectionReady && status.textContent === this.strings.chooseMain) status.textContent = '';
      const included = this.hasIncludedItems;
      const pending = included && this.rows.find(row => row.included && !row.variant);
      const label = pending ? this.text('choose', { OPTION: this.strings[pending.group] }) : this.strings.addSet;
      const buttons = [...this.form.elements].filter(element => element.type === 'submit');
      if (this.mainSizeChosen) this.scope?.querySelector('.product-info .option_downcase-size')?.classList.remove('disabled_selected_block');
      for (const button of buttons) {
        // custom.js suppresses clicks on this legacy class even when the form is valid.
        if (this.mainSizeChosen) button.classList.remove('select_size_btn');
        if (!this.mainSelectionReady && (included || button.dataset.completeSetLabel)) {
          delete button.dataset.completeSetLabel;
          const buttonLabel = String((this.mainSelectionCleared || !this.mainVariantId || !this.mainSizeChosen ? window.themeVariables.strings.chooseOptions : window.themeVariables.strings.soldOutButton) || this.strings.chooseMain || '');
          if (button.textContent !== buttonLabel) button.textContent = buttonLabel;
          button.disabled = true;
        } else if (included && this.mainSelectionReady) {
          button.dataset.completeSetLabel = 'true';
          if (button.textContent !== label) button.textContent = label;
          // Keep the button usable to expose/focus unresolved options.
          button.disabled = !!this.busy || !!this.uncertain;
        } else if (button.dataset.completeSetLabel) {
          delete button.dataset.completeSetLabel;
          const template = button.closest('buy-buttons')?.getAttribute('template') || '';
          button.textContent = this.mainAvailable ? template.includes('pre-order') ? window.themeVariables.strings.preOrderButton : window.themeVariables.strings.addToCartButton : window.themeVariables.strings.soldOutButton;
          button.disabled = !this.mainAvailable || !!this.busy || !!this.uncertain;
        }
      }
      // CSS composes with native variant/Amazon visibility instead of overriding it.
      this.scope?.classList.toggle('complete-set-has-items', included);
      const total = this.querySelector('[data-set-total]');
      total.hidden = !included;
      if (included) {
        const quantity = Math.max(1, Number(new FormData(this.form).get('quantity') || 1));
        const selected = this.rows.filter(row => row.included);
        const amount = this.mainPrice * quantity + selected.reduce((sum, row) => sum + Number(row.variant?.price ?? row.product.price), 0);
        total.textContent = `${this.text('subtotal', { COUNT: quantity + selected.length })} · ${this.money.format(amount / 100)}${pending ? ` · ${this.strings.estimate}` : ''}`;
      }
    }

    validateSelection() {
      if (!this.mainSelectionReady) {
        this.querySelector('[data-set-status]').textContent = this.mainSelectionCleared || !this.mainVariantId || this.mainAvailable ? this.strings.chooseMain : window.themeVariables.strings.unavailableButton;
        const picker = this.scope.querySelector('.option_downcase-size') || this.scope.querySelector('variant-picker');
        picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        picker?.querySelector('input, button')?.focus({ preventScroll: true });
        return false;
      }
      if (!this.hasIncludedItems) return !this.uncertain;
      const row = this.rows.find(row => row.included && !row.variant);
      if (!row && !this.uncertain) return true;
      this.querySelector('[data-set-disclosure]').open = true;
      if (row) {
        this.openRow(row);
        row.message.textContent = this.missing(row);
        row.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const missing = row.data?.options.findIndex((option, index) => !row.choices[index]);
        (row.options.querySelector(`[data-option-index="${missing}"] button`) || row.options.querySelector('button'))?.focus({ preventScroll: true });
      }
      return false;
    }

    getCartItems() {
      return this.mainSelectionReady && this.hasIncludedItems ? this.rows.filter(row => row.included && row.variant).map(row => ({ id: row.variant.id, quantity: 1, properties: Object.fromEntries(Object.entries(row.variant.properties || {}).filter(([, value]) => value !== '')) })) : [];
    }

    submitWithCompanions(event, form) {
      if (this.uncertain) {
        event.preventDefault();
        return true;
      }
      if (!this.hasIncludedItems && this.mainVariantId) return false;
      event.preventDefault();
      if (form._cartSubmitting) return true;
      if (!this.validateSelection()) return true;
      if (!form.checkValidity()) {
        form.reportValidity();
        return true;
      }
      this.submitBundle(form);
      return true;
    }

    async submitBundle(form) {
      if (form !== this.form || !this.validateSelection()) return;
      const mainVariantId = this.mainVariantId;
      if (!mainVariantId) return;
      const companions = this.getCartItems();
      if (!companions.length) return;
      const formData = new FormData(form);
      for (const name of Array.from(new Set(formData.keys()))) {
        const property = name.match(/^properties\[(.*)\]$/);
        if (!['id', 'quantity', 'selling_plan'].includes(name) && !property) continue;
        const indexedName = property ? `items[0][properties][${property[1]}]` : `items[0][${name}]`;
        formData.getAll(name).forEach(value => formData.append(indexedName, value));
        formData.delete(name);
      }
      if (!formData.has('items[0][quantity]')) formData.set('items[0][quantity]', '1');
      companions.forEach((item, index) => {
        formData.set(`items[${index + 1}][id]`, String(item.id));
        formData.set(`items[${index + 1}][quantity]`, String(item.quantity));
        for (const [key, value] of Object.entries(item.properties || {})) formData.append(`items[${index + 1}][properties][${key}]`, value);
      });
      const submitButtons = Array.from(form.elements).filter(button => button.type === 'submit');
      const disabledStates = submitButtons.map(button => button.disabled);
      let sectionsToBundle = [];
      form._cartSubmitting = true;
      const cartJson = (url, options = {}) => fetchWithTimeout(url, options, 15000, async response => ({ response, json: await response.json() }));
      const reportError = message => this.reconcileError(message || this.dataset.cartError || window.themeVariables.strings.unavailableButton);
      const reconcileCart = async () => {
        const { response: cartResponse, json: cart } = await cartJson(`${Shopify.routes.root}cart.js`, { cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart reconciliation failed');
        if (sectionsToBundle.length) {
          const sectionUrl = new URL(window.location.href);
          sectionUrl.searchParams.set('sections', sectionsToBundle.join(','));
          const { response: sectionsResponse, json: sections } = await cartJson(sectionUrl, { cache: 'no-store' });
          if (!sectionsResponse.ok) throw new Error('Cart sections reconciliation failed');
          cart.sections = sections;
          if (sectionsToBundle.some(section => !cart.sections?.[section])) throw new Error('Cart sections unavailable');
        } else cart.sections = {};
        document.documentElement.dispatchEvent(new window.CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'cart:reconcile', cart } }));
      };
      try {
        this.handleSubmitState(true);
        submitButtons.forEach(button => {
          button.setAttribute('disabled', 'disabled');
          button.setAttribute('aria-busy', 'true');
        });
        document.documentElement.dispatchEvent(new window.CustomEvent('theme:loading:start', { bubbles: true }));
        document.documentElement.dispatchEvent(new window.CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        sectionsToBundle = Array.from(new Set(sectionsToBundle));
        formData.set('sections', sectionsToBundle.join(','));
        formData.set('sections_url', `${Shopify.routes.root}variants/${mainVariantId}`);
        const { response, json: responseJson } = await cartJson(`${Shopify.routes.root}cart/add.js`, {
          body: formData,
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) {
          await reconcileCart();
          reportError(responseJson.description);
          return;
        }
        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }
        const { response: cartResponse, json: cartContent } = await cartJson(`${Shopify.routes.root}cart.js`, { cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        cartContent.sections = responseJson.sections;
        if (sectionsToBundle.some(section => !cartContent.sections?.[section])) throw new Error('Cart sections unavailable');
        form.dispatchEvent(new window.CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: Object.hasOwn(responseJson, 'items') ? responseJson.items : [responseJson], cart: cartContent, onSuccessDo: formData.get('on_success') }
        }));
        document.documentElement.dispatchEvent(new window.CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: formData.get('on_success'), cart: cartContent }
        }));
      } catch {
        try {
          await reconcileCart();
        } catch {
          document.documentElement.dispatchEvent(new window.CustomEvent('cart:refresh', { bubbles: true }));
        }
        this.reconcileError(this.dataset.cartUnknown || window.themeVariables.strings.unavailableButton);
      } finally {
        form._cartSubmitting = false;
        submitButtons.forEach((button, index) => {
          button.disabled = disabledStates[index];
          button.removeAttribute('aria-busy');
        });
        this.handleSubmitState(false);
        this.syncButtons();
        document.documentElement.dispatchEvent(new window.CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }

    handleSubmitState(busy) {
      this.busy = busy;
      for (const control of this.querySelectorAll('input, button')) if (control.type !== 'submit') control.disabled = busy;
      this.syncButtons();
    }

    reconcileError(message) {
      this.uncertain = true;
      this.querySelector('[data-set-status]').textContent = message;
      this.querySelector('[data-set-cart]').hidden = false;
      this.syncButtons();
    }
  }
  if (!customElements.get('jc-complete-set')) customElements.define('jc-complete-set', CompleteSet);
})();
